import { useEffect, useState } from "react";
import Cropper from "react-easy-crop";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Slider } from "./ui/slider";
import { Loader2, RotateCcw } from "lucide-react";

const ASPECTS = [{ label: "Wide (16:9)", v: 16 / 9 }, { label: "Landscape (3:2)", v: 3 / 2 }, { label: "Square", v: 1 }, { label: "Free", v: null }];

async function cropToFile(src, area, rotation, filename) {
  const img = await new Promise((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = src; });
  const rad = (rotation * Math.PI) / 180;
  const bw = Math.abs(Math.cos(rad) * img.width) + Math.abs(Math.sin(rad) * img.width * (img.height / img.width));
  const bh = Math.abs(Math.sin(rad) * img.width) + Math.abs(Math.cos(rad) * img.height);
  const c = document.createElement("canvas");
  c.width = bw; c.height = bh;
  const ctx = c.getContext("2d");
  ctx.translate(bw / 2, bh / 2); ctx.rotate(rad); ctx.translate(-img.width / 2, -img.height / 2);
  ctx.drawImage(img, 0, 0);
  const out = document.createElement("canvas");
  const scale = Math.min(1, 1920 / area.width);
  out.width = Math.round(area.width * scale); out.height = Math.round(area.height * scale);
  out.getContext("2d").drawImage(c, area.x, area.y, area.width, area.height, 0, 0, out.width, out.height);
  const blob = await new Promise((r) => out.toBlob(r, "image/jpeg", 0.88));
  return new File([blob], filename.replace(/\.[^.]+$/, "") + ".jpg", { type: "image/jpeg" });
}

export function ImageCropDialog({ file, defaultAspect = 16 / 9, onDone, onCancel }) {
  const [src, setSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [aspect, setAspect] = useState(defaultAspect);
  const [area, setArea] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    setSrc(url); setCrop({ x: 0, y: 0 }); setZoom(1); setRotation(0);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const apply = async () => {
    setBusy(true);
    try { onDone(await cropToFile(src, area, rotation, file.name)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!file} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="max-w-2xl" data-testid="crop-dialog">
        <DialogHeader><DialogTitle>Crop & straighten</DialogTitle></DialogHeader>
        <div className="relative h-[360px] bg-[#0F2744] rounded-xl overflow-hidden">
          {src && <Cropper image={src} crop={crop} zoom={zoom} rotation={rotation} aspect={aspect || undefined}
            onCropChange={setCrop} onZoomChange={setZoom} onRotationChange={setRotation}
            onCropComplete={(_, px) => setArea(px)} />}
        </div>
        <div className="flex flex-wrap gap-2">
          {ASPECTS.map((a) => (
            <button key={a.label} type="button" onClick={() => setAspect(a.v)} data-testid={`crop-aspect-${a.label.split(" ")[0].toLowerCase()}`}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${aspect === a.v ? "bg-[#0F2744] text-white border-[#0F2744]" : "bg-white text-slate-600 border-slate-200"}`}>{a.label}</button>
          ))}
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><div className="text-xs text-slate-500 mb-2">Zoom</div><Slider min={1} max={4} step={0.01} value={[zoom]} onValueChange={([v]) => setZoom(v)} data-testid="crop-zoom" /></div>
          <div>
            <div className="text-xs text-slate-500 mb-2 flex justify-between">Straighten <span>{rotation.toFixed(1)}°</span></div>
            <Slider min={-45} max={45} step={0.1} value={[rotation]} onValueChange={([v]) => setRotation(v)} data-testid="crop-rotation" />
          </div>
        </div>
        <div className="flex justify-between items-center pt-1">
          <Button type="button" variant="ghost" size="sm" onClick={() => { setZoom(1); setRotation(0); setCrop({ x: 0, y: 0 }); }} data-testid="crop-reset"><RotateCcw className="h-4 w-4 mr-1" /> Reset</Button>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={onCancel} data-testid="crop-cancel">Cancel</Button>
            <Button type="button" onClick={apply} disabled={busy || !area} className="bg-amber-500 hover:bg-amber-400 text-[#0F2744] font-bold" data-testid="crop-apply">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Use this crop"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
